// frontend/src/pages/admin/McChecklistDocxTemplatesAdmin.tsx
import React, { useMemo, useState } from "react";
import { Badge, Button, Card, Group, Stack, Text, Title } from "@mantine/core";
import { IconCheck, IconDownload, IconUpload, IconX } from "@tabler/icons-react";
import { useAppDispatch } from "../../../hooks";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import {
  useGetChecklistMcTemplatesDocxDownloadQuery,
  useGetChecklistMcTemplatesDocxQuery,
  usePostChecklistMcTemplatesUploadDocxMutation,
} from "../../../../openapi";
import { MC_CHECKLIST_TEMPLATES_DOCX } from "../../../mc-shared/enums";
import { FailedAlert, MCLoader } from "../../../components";
import { downloadBase64File, readFileAsBase64 } from "../../fileHandlerUtils";
import { StatusExistPill } from "../../../components/StatusExistPill";

const DOCX_CONTENT_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document" as const;

type TemplateFilename = typeof MC_CHECKLIST_TEMPLATES_DOCX[number];



export const McChecklistDocxTemplatesAdmin: React.FC = () => {
  const dispatch = useAppDispatch();

  // list
  const { data, isLoading, isError, refetch } = useGetChecklistMcTemplatesDocxQuery();

  // upload
  const [uploadDocx, uploadState] = usePostChecklistMcTemplatesUploadDocxMutation();

  // download (triggered by state + skip)
  const [downloadFilename, setDownloadFilename] = useState<TemplateFilename | null>(null);
  const {
    data: downloadData,
    isFetching: isDownloading,
    isError: isDownloadError,
  } = useGetChecklistMcTemplatesDocxDownloadQuery(
    downloadFilename ? { filename: downloadFilename } : (undefined as any),
    { skip: downloadFilename == null },
  );

  // perform browser download when data arrives
  React.useEffect(() => {
    if (!downloadData) return;
    try {
      downloadBase64File(downloadData.filename, downloadData.base64, downloadData.contentType);
    } finally {
      setDownloadFilename(null);
    }
  }, [downloadData]);

  const metaByFilename = useMemo(() => {
    const m = new Map<string, any>();
    for (const t of data?.templates ?? []) m.set(t.filename, t);
    return m;
  }, [data]);

  const upload = async (filename: TemplateFilename, e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.currentTarget.files?.[0];
      if (!file) return;

      // reset input ASAP so same file can be selected again
      e.currentTarget.value = "";

      // super simple validation
      if (file.type && file.type !== DOCX_CONTENT_TYPE) {
        throw new Error(`Ugyldig filtype: ${file.type}`);
      }
      if (!file.name.toLowerCase().endsWith(".docx")) {
        throw new Error("Filen må være en .docx");
      }

      const base64 = await readFileAsBase64(file);

      await uploadDocx({
        body: {
          file: {
            filename,
            contentType: DOCX_CONTENT_TYPE,
            base64,
          },
        },
      }).unwrap();

      dispatch(setShowSuccessMessageThunk(`Malen er lastet opp: ${filename}`));
      await refetch();
    } catch (err: any) {
      console.error(err);
      // keep it admin-simple: show a toast message
      dispatch(setShowSuccessMessageThunk(`Feil: ${err?.message ?? String(err)}`));
    }
  };

  const download = (filename: TemplateFilename) => {
    setDownloadFilename(filename);
  };

  const hasError = isError || uploadState.isError || isDownloadError;

  return (
    <Card withBorder shadow="sm" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Title order={4}>DOCX-maler – sjekkliste (MC)</Title>
          <Button variant="light" onClick={() => refetch()} disabled={isLoading}>
            Oppdater
          </Button>
        </Group>

        {(isLoading || uploadState.isLoading || isDownloading) && <MCLoader />}
        {hasError && <FailedAlert />}

        <Stack gap="sm">
          {MC_CHECKLIST_TEMPLATES_DOCX.map((filename) => {
            const exists = Boolean(metaByFilename.get(filename)?.exists);

            return (
              <Card key={filename} withBorder shadow="sm" p="md">
                <Group justify="space-between" align="center">
                  <Stack gap={2}>
                    <Text fw={600}>{filename}</Text>
                    <StatusExistPill exists={exists} />
                  </Stack>

                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconDownload size={16} />}
                      disabled={!exists}
                      onClick={() => download(filename)}
                    >
                      Last ned
                    </Button>

                    <label>
                      <input
                        type="file"
                        hidden
                        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={(e) => upload(filename, e)}
                      />
                      <Button
                        size="xs"
                        variant="filled"
                        leftSection={<IconUpload size={16} />}
                        disabled={uploadState.isLoading}
                        component="span"
                      >
                        Last opp
                      </Button>
                    </label>
                  </Group>
                </Group>
              </Card>
            );
          })}
        </Stack>

        <Text size="sm" c="dimmed">
          Opplasting overskriver eksisterende blob med samme navn.
        </Text>
      </Stack>
    </Card>
  );
};
